import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import httpService from 'services/httpService';
import log from "loglevel";
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';

const FieldSelectionModal = ({ show, onHide, downloadFunction, lab_Id }) => {
    const { courseId } = useParams();
    const [availableFields, setAvailableFields] = useState([]);
    const [preferredFields, setPreferredFields] = useState([]);
    const [selectedFields, setSelectedFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [savingDefaultData, setSavingDefaultData] = useState(false);

    useEffect(() => {
        log.log({ selectedFields });
    }, [selectedFields]);

    useEffect(() => {
        const fetchFields = async () => {
            setLoading(true);
            setError('');
            try {
                const availableResponse = await httpService.get(
                    `/evalpro/submissions/download_all_csv/${lab_Id}/available_fields/?courseId=${courseId}`
                );
                const fields = availableResponse.data || [];
                setAvailableFields(fields);
                let isStateSet = false;
                try {
                    const userPreferredFieldsRequest = await httpService.get(
                        `/user/api/user-prefs/get_programming_activity_csv_export_preferences/`
                    )
                    const userPreferredFields = userPreferredFieldsRequest.data;
                    if (Array.isArray(userPreferredFields) && userPreferredFields.length > 0) {
                        setPreferredFields(userPreferredFields);
                        setSelectedFields(userPreferredFields);
                        isStateSet = true;
                    }
                } catch (error) {
                    log.error(error);
                }
                if (!isStateSet) {
                    setPreferredFields([]);
                    setSelectedFields(fields.length > 0 ? fields : []);
                }
            } catch (err) {
                setError('Failed to fetch fields. Please try again.');
                log.error('Error fetching fields:', err);
            } finally {
                setLoading(false);
            }
        };

        if (show) {
            fetchFields();
        } else {
            // Reset state when modal is closed
            setAvailableFields([]);
            setPreferredFields([]);
            setSelectedFields([]);
            setLoading(true);
            setError('');
        }
    }, [show, lab_Id]);

    const handleFieldChange = (event) => {
        const { value, checked } = event.target;
        setSelectedFields((prevSelected) =>
            checked ? [...prevSelected, value] : prevSelected.filter((field) => field !== value)
        );
    };

    const handleSelectAll = (event) => {
        setSelectedFields(event.target.checked ? availableFields : []);
    };

    const handleSetDefault = async () => {
        try {
            setSavingDefaultData(true);
            await httpService.post('/user/api/user-prefs/save_programming_activity_csv_export_preferences/', {
                preferences: selectedFields
            });
            log.log('Preferred fields set successfully!');
            displayGlobalMessage('Preferred fields saved successfully.', 'success');
        } catch (err) {
            displayGlobalMessage('Failed to set preferred fields. Please try again.', 'error');
            log.error('Error setting preferred fields:', err);
        } finally {
            setSavingDefaultData(false);
        }
    };

    const handleDownload = (e) => {
        if (!selectedFields || selectedFields.length === 0) {
            displayGlobalMessage(`You have selected ${selectedFields.length} fields, please select at least 1.`)
            return;
        }
        downloadFunction?.(e, selectedFields)
    }

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Select CSV Columns</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="d-flex justify-content-center">
                        <Spinner animation="border" role="status" />
                    </div>
                ) : error ? (
                    <p className="text-danger">{error}</p>
                ) : (
                    <Form>
                        <Alert key={"info"} variant={"info"}>
                            <strong>Please Note:</strong>
                            <p className="mb-0">
                                For the column titles listed below, any title that starts with 'Testcase' will have a separate column for each testcase.
                            </p>
                        </Alert>

                        <Form.Check
                            type="checkbox"
                            label="Select All"
                            checked={selectedFields.length === availableFields.length && availableFields.length > 0}
                            onChange={handleSelectAll}
                        />

                        <div style={{ display: "grid", gridTemplateColumns: 'repeat(4, 1fr)', marginTop: '10px' }}>
                            {availableFields.map((field) => (
                                <Form.Check
                                    key={field}
                                    type="checkbox"
                                    label={field}
                                    value={field}
                                    checked={selectedFields.includes(field)}
                                    onChange={handleFieldChange}
                                />
                            ))}
                        </div>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
                {!loading && !error && (
                    <Button variant="primary" disabled={savingDefaultData} onClick={handleSetDefault}>
                        {
                            savingDefaultData ? <span className='d-flex gap-2 align-items-center'>
                                <Spinner size='sm' animation='border' /> Saving ...
                            </span> : 'Set as Default'
                        }
                    </Button>
                )}
                <Button disabled={loading || error} variant="primary" onClick={handleDownload}>
                    Download
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default FieldSelectionModal;
